import { useRef, useState, useEffect } from "react";
import { motion, useScroll } from "framer-motion";
import {
  Reveal,
  SectionNo,
  Hairline,
  MaskReveal,
  PlaceholderTag,
} from "@/components/primitives";
import BigSectionNo from "@/components/BigSectionNo";
import LivingImage from "@/components/LivingImage";
import { useCapabilities } from "@/lib/device";
import { placeholders } from "@/config/site";

const STEPS = [
  {
    n: "01",
    title: "Send the library",
    body: "You send what you have. We tell you honestly what will move well, and what won't, before you commit to anything.",
    still: "W01",
    film: "W01",
  },
  {
    n: "02",
    title: "Agree the set",
    body: "We fix the exact image set and the channel each film is for. Nothing begins until the brief is agreed.",
    still: "W02",
    film: "W02",
  },
  {
    n: "03",
    title: "First cut",
    body: "We build the films and you see a first cut, with revision rounds included and stated up front.",
    still: "W03",
    film: "W03",
  },
  {
    n: "04",
    title: "Delivery",
    body: "Masters, cutdowns and every ratio, licensed, named for channel, ready to publish.",
    still: "W04",
    film: "W04",
  },
];

export default function Process() {
  const ref = useRef(null);
  const caps = useCapabilities();
  const [active, setActive] = useState(0);

  // Desktop-only pin. On mobile / coarse-pointer / reduced-motion / narrow
  // viewports we fall back to a stacked poster grid — the previous version
  // gated on `!reduced` only, which left phones stuck inside a 400vh sticky
  // block.
  const canPin = caps.ready && caps.webgl;

  // useScroll must be called every render (hook order) and always operates
  // on the same ref, which we attach to the section unconditionally below.
  // When canPin is false the section is only its natural height so progress
  // simply sweeps 0→1 as the section passes, and we don't use `active` for
  // anything visible — the stacked grid ignores it.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      // Small entry/exit pad so step 01 holds while the section is settling
      // into pin, and step 04 lingers before release.
      const remapped = (v - 0.06) / 0.88;
      const idx = Math.min(
        STEPS.length - 1,
        Math.max(0, Math.floor(remapped * STEPS.length)),
      );
      setActive(idx);
    });
    return unsub;
  }, [scrollYProgress]);

  // Click a step → scroll to the middle of its viewport-height slice.
  const jumpTo = (i) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const sectionTop = window.scrollY + rect.top;
    const vh = window.innerHeight;
    // Match the remapping in the progress listener: put us at remapped=(i+0.5)/N.
    // For a section of height N*vh with sticky vh child, scroll-progress goes
    // from 0 (top hits viewport top) to 1 (bottom hits viewport bottom); we
    // want scrollY = sectionTop + ((i+0.5)/N * 0.88 + 0.06) * (N-1) * vh.
    const totalScrollable = (STEPS.length - 1) * vh;
    const remapped = (i + 0.5) / STEPS.length;
    const rawV = remapped * 0.88 + 0.06;
    const target = sectionTop + rawV * totalScrollable;
    window.scrollTo({ top: target, behavior: "smooth" });
  };

  return (
    <section
      id="process"
      ref={ref}
      data-testid="process"
      className={`relative warm-radial ${
        canPin ? "" : "overflow-hidden px-6 py-24 sm:px-10 md:py-32"
      }`}
      style={canPin ? { height: `${STEPS.length * 100}vh` } : undefined}
    >
      {canPin ? (
        <PinnedLayout active={active} jumpTo={jumpTo} />
      ) : (
        <StackedLayout />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

function PinnedLayout({ active, jumpTo }) {
  return (
    <>
      <BigSectionNo n="06" />
      <div className="sticky top-0 flex h-screen items-center px-6 sm:px-10">
        <div className="relative z-10 mx-auto grid w-full max-w-[1280px] gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          {/* Sticky media column: cross-dissolves between LivingImage frames.
              Fixed viewport-relative height so the tile always fits inside
              the pin (no top/bottom crop while scrolling). Width flows from
              the grid column; object-cover handles portrait video framing. */}
          <div className="relative order-2 mx-auto w-full max-w-[380px] overflow-hidden rounded-[2px] border border-hairline aspect-[4/5] lg:order-1 lg:mx-0 lg:aspect-auto lg:h-[68vh] lg:max-w-none lg:w-full">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                className="absolute inset-0"
                initial={false}
                animate={{ opacity: active === i ? 1 : 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                <LivingImage
                  still={s.still}
                  film={s.film || undefined}
                  sizes="(min-width: 1024px) 45vw, 100vw"
                  wrapperClassName="absolute inset-0"
                  className="h-full w-full object-cover"
                  testId={`process-step-${s.n}`}
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                <div className="on-dark absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
                  <span className="font-mono text-[10px] tracking-[0.28em] text-gold">
                    {s.n}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.28em] text-mutedText">
                    {i + 1} / {STEPS.length}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Copy column: shows the active step */}
          <div className="order-1 flex flex-col justify-center lg:order-2">
            <Hairline className="mb-8" />
            <SectionNo n="06" label="How it works" />
            <h2
              className="display mt-6 max-w-lg text-primaryText"
              style={{
                fontSize: "clamp(2rem, 4.6vw, 3.5rem)",
                lineHeight: 1.03,
                letterSpacing: "-0.015em",
                fontWeight: 300,
              }}
            >
              Four steps. No one comes to your property.
            </h2>

            <div className="mt-10 space-y-6">
              {STEPS.map((s, i) => {
                const isActive = active === i;
                return (
                  <motion.button
                    key={s.n}
                    type="button"
                    onClick={() => jumpTo(i)}
                    className="group grid w-full grid-cols-[auto_1fr] gap-5 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/60"
                    initial={false}
                    animate={{ opacity: isActive ? 1 : 0.32 }}
                    whileHover={{ opacity: isActive ? 1 : 0.6 }}
                    transition={{ duration: 0.4 }}
                    data-testid={`process-jump-${s.n}`}
                    aria-label={`Show step ${s.n}: ${s.title}`}
                  >
                    <div className="flex flex-col items-center pt-1">
                      <span
                        className={`block h-3 w-3 rounded-full border transition-all duration-500 ${
                          isActive
                            ? "border-gold bg-gold scale-110"
                            : "border-gold/40 bg-transparent group-hover:border-gold/80"
                        }`}
                      />
                      {i < STEPS.length - 1 && (
                        <span className="mt-1 h-8 w-px bg-gold/20" />
                      )}
                    </div>
                    <div>
                      <div className="font-mono text-[11px] tracking-widest text-gold">
                        {s.n}
                      </div>
                      <h3 className="display mt-1 text-2xl text-primaryText transition-colors duration-300 group-hover:text-gold/90">
                        {s.title}
                      </h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-mutedText">
                        {s.body}
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            <p className="mt-10 border-t border-hairline pt-6 font-mono text-xs tracking-wide text-mutedText">
              First cut in {placeholders.firstCutDays}
              <PlaceholderTag /> · full delivery in {placeholders.turnaroundDays}
              <PlaceholderTag /> · {placeholders.revisionRounds} included
              <PlaceholderTag />
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function StackedLayout() {
  return (
    <>
      <BigSectionNo n="06" />
      <div className="relative mx-auto max-w-[1280px]">
        <Hairline className="mb-16" />
        <Reveal>
          <SectionNo n="06" label="How it works" />
        </Reveal>
        <h2
          className="display mt-8 max-w-2xl text-primaryText"
          style={{
            fontSize: "clamp(2.25rem, 5vw, 4rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.015em",
            fontWeight: 300,
          }}
        >
          <MaskReveal>Four steps. No one</MaskReveal>
          <MaskReveal delay={0.1}>comes to your property.</MaskReveal>
        </h2>
        <div className="mt-14 grid gap-8">
          {STEPS.map((s) => (
            <div key={s.n} className="grid gap-6 sm:grid-cols-[1fr_1fr]">
              <div className="relative aspect-[3/4] overflow-hidden rounded-[2px] border border-hairline">
                <LivingImage
                  still={s.still}
                  film={s.film || undefined}
                  sizes="(min-width: 640px) 50vw, 100vw"
                  wrapperClassName="absolute inset-0"
                  className="h-full w-full object-cover"
                  testId={`process-step-mobile-${s.n}`}
                />
              </div>
              <div>
                <div className="font-mono text-[11px] tracking-widest text-gold">
                  {s.n}
                </div>
                <h3 className="display mt-3 text-2xl text-primaryText">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-mutedText">
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-16 border-t border-hairline pt-8 font-mono text-xs tracking-wide text-mutedText">
          First cut in {placeholders.firstCutDays}
          <PlaceholderTag /> · full delivery in {placeholders.turnaroundDays}
          <PlaceholderTag /> · {placeholders.revisionRounds} included
          <PlaceholderTag />
        </p>
      </div>
    </>
  );
}
